import { useMemo } from "react";

import type { UploadScope, UploadScopeType } from "@/components/tiptap/utils/uploadScope";
import type { DocumentModel } from "@/models/DocumentModel";

/**
 * Scopes an image/file node view resolves its blob through: `scope` is the
 * editor's upload scope (falling back to the editor's document) and
 * `sourceScope` — set while a copied node is not yet re-attached — is the
 * scope it was copied from. Legacy content has no sourceType; those uids
 * are always documents. Memoized so the objects are safe in effect deps.
 */
export function useUploadScopes(
  doc: DocumentModel,
  uploadScope: UploadScope | undefined,
  { sourceUid, sourceType }: { sourceUid: string | null; sourceType: string | null },
) {
  const uid = uploadScope?.uid ?? doc.id;
  const type = uploadScope?.type ?? "document";

  const scope = useMemo<UploadScope | undefined>(
    () => (uid ? { type, uid } : undefined),
    [type, uid],
  );
  const sourceScope = useMemo(
    () => getSourceScope({ sourceUid, sourceType }),
    [sourceUid, sourceType],
  );

  return { scope, sourceScope, resolveScope: sourceScope ?? scope };
}

function getSourceScope({
  sourceUid,
  sourceType,
}: {
  sourceUid: string | null;
  sourceType: string | null;
}): UploadScope | undefined {
  return sourceUid
    ? { type: (sourceType as UploadScopeType | null) ?? "document", uid: sourceUid }
    : undefined;
}

/** Non-hook equivalent of `useUploadScopes(...).resolveScope`, for code outside a node view. */
export function resolveUploadScope(
  doc: DocumentModel,
  uploadScope: UploadScope | undefined,
  source: { sourceUid: string | null; sourceType: string | null },
): UploadScope | undefined {
  const uid = uploadScope?.uid ?? doc.id;
  const scope = uid ? { type: uploadScope?.type ?? "document", uid } : undefined;
  return getSourceScope(source) ?? scope;
}
